import { GeoLang } from '@utils/server/getGeoFromPath';
import axios from 'axios';
import { getStorageJSON, setStorageJSON } from '@functions/storage-tools';
import { geoLangs } from '@functions/geo-functions';

const locationDictionary: Array<GeoLang> = geoLangs;

const defaultLocation = locationDictionary.find((geo) => geo.isDefault)!;

class Geo implements GeoLang {
  static defaultLatLong = {
    latitude: 40.723856,
    longitude: -111.855947,
  };

  static allLocations = locationDictionary.map(({ locale }) => new Geo({ locale }));

  static getList(key: keyof GeoLang) {
    return locationDictionary.map((loc) => loc[key]);
  }

  static lookup(index: Partial<GeoLang>, key: keyof GeoLang) {
    const [locationKey, testValue] = Object.entries(index)[0];
    for (const i in locationDictionary) {
      const location = locationDictionary[i];
      if (testValue === location[locationKey as keyof typeof location]) {
        return location[key];
      }
    }
    return '';
  }

  static routeCountry = async (lat: number, long: number) => {
    try {
      const { data } = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${encodeURI(
          lat.toString(),
        )},${encodeURI(long.toString())}&sensor=false&key=${process.env.GOOGLE_MAPS_API_KEY}`,
      );
      if (data && data.results) {
        let reroute = null;
        data.results.forEach(({ address_components }: any) => {
          if (address_components) {
            address_components.forEach(({ types, short_name }: any) => {
              if (types.includes('country')) {
                reroute = short_name;
              }
            });
          }
        });

        if (reroute) {
          const country = this.lookup({ productGeo: reroute }, 'geoLoc');
          if (country !== '/us/en') {
            return country;
          }
          return null;
        }
        return null;
      }
    } catch (error) {
      console.error(error);
    }
  };

  static getAndSetLocation = async () => {
    let lat = this.defaultLatLong.latitude;
    let long = this.defaultLatLong.longitude;
    const currentLocation = await getStorageJSON('currentLocation');
    if (currentLocation.latitude && currentLocation.longitude) {
      const { latitude, longitude } = currentLocation;
      lat = latitude;
      long = longitude;
    } else if (window && window.navigator) {
      window.navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (pos.coords) {
            const { latitude, longitude } = pos.coords;
            const location = { latitude, longitude };
            await setStorageJSON('currentLocation', location);
            lat = latitude;
            long = longitude;
            const country = await this.routeCountry(lat, long);
            if (country) {
              // @ts-expect-error it works
              window.location = country;
            }
          }
        },
        async (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            const location = {
              latitude: lat,
              longitude: long,
            };
            await setStorageJSON('currentLocation', location);
          }
        },
      );
    }
    return { latitude: lat, longitude: long };
  };

  static isEqual = (geo1: Geo, geo2: Geo) => {
    return geo1.locale === geo2.locale;
  };

  static isBazaarvoiceEnabled = (locale: string) => {
    return !!this.lookup({ locale }, 'enableBazaarvoice');
  };

  static fromPath = (path: string) => {
    const lang = locationDictionary.find((loc) => path.startsWith(loc.geoLoc)) ?? defaultLocation;
    return new Geo({ locale: lang.locale });
  };

  country = defaultLocation.country;
  enableBazaarvoice = defaultLocation.enableBazaarvoice;
  errorPage = defaultLocation.errorPage;
  flag = defaultLocation.flag;
  generalPage = defaultLocation.generalPage;
  geoLoc = defaultLocation.geoLoc;
  locale = defaultLocation.locale;
  name = defaultLocation.name;
  productGeo = defaultLocation.productGeo;
  exists = true;
  currency = defaultLocation.currency;
  isDefault = true;

  constructor(props: Partial<GeoLang>) {
    try {
      const [key, value] = Object.entries(props)[0];
      const location: GeoLang | undefined = locationDictionary.find(
        (loc) => loc[key as keyof typeof loc] === value,
      );

      if (location) {
        Object.assign(this, location);
      } else {
        this.exists = false;
      }
    } catch (e) {
      console.error('Error constructing Geo class', e);
    }
  }

  get info() {
    return {
      name: this.name,
      homeLink: this.geoLoc,
      flag: this.flag,
    };
  }
}

export default Geo;
